import { useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Check, Copy } from "lucide-react";
import { Card } from "../../../components/Card";
import { SectionTitle } from "../../../components/SectionTitle";
import { Button } from "../../../components/Button";
import { LoadingContainer } from "../../../components/LoadingContainer";
import { SidebarPanel } from "../../../components/SidebarPanel";
import { FormGroup } from "../../../components/FormGroup";
import { FormLabel } from "../../../components/FormLabel";
import { SelectInput } from "../../../components/SelectInput";
import { InfoBox } from "../../../components/InfoBox";
import { IntegrationForm } from "../IntegrationForm";
import { useGetIntegrationsQuery } from "../../../query/integrationsQuery";
import { useGetStoreQuery } from "../../../query/storesQuery";
import { spacing } from "@styling/sizing";
import colors from "@styling/colors";
import { fontSizes } from "@styling/fonts";
import {
  ACQUIRER_INTEGRATIONS,
  IntegrationType,
} from "@KassaroAB/shared/types/enums/IntegrationType";
import type {
  IntegrationViva,
  RestIntegration,
} from "@KassaroAB/shared/types/integrations/integration";
import type { IntegrationId, StoreId } from "@KassaroAB/shared/types/ids";

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  margin-bottom: ${spacing.x2}px;
  flex-wrap: wrap;
`;

const AcquirerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x1}px;
`;

const AcquirerRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.x2}px;
  width: 100%;
  padding: ${spacing.x2}px;
  border: 1px solid ${colors.border};
  border-radius: 8px;
  background-color: ${colors.background};

  &:hover {
    background-color: ${colors.backgroundDark};
  }
`;

const AcquirerOpenButton = styled.button`
  display: flex;
  flex: 1;
  min-width: 0;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
`;

const AcquirerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
  min-width: 0;
`;

const CopyOnboardingButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${spacing.x05}px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  font-size: ${fontSizes.small}px;
  font-weight: 600;
  color: ${colors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

const AcquirerName = styled.div`
  font-size: ${fontSizes.regular}px;
  font-weight: 600;
  color: ${colors.text};
`;

const AcquirerMeta = styled.div`
  font-size: ${fontSizes.small}px;
  color: ${colors.textMuted};
`;

const EmptyText = styled.p`
  margin: 0;
  color: ${colors.textMuted};
`;

const isAcquirerIntegration = (
  integration: RestIntegration,
): boolean =>
  (ACQUIRER_INTEGRATIONS as readonly IntegrationType[]).includes(
    integration.type,
  );

const getVivaOnboardingUrl = (
  integration: RestIntegration,
): string | undefined => {
  if (integration.type !== IntegrationType.VIVA) {
    return undefined;
  }
  const url = (integration as IntegrationViva).onboardingUrl;
  return typeof url === "string" && url.length > 0 ? url : undefined;
};

const ACQUIRER_TYPE_OPTIONS = [
  { value: IntegrationType.VIVA, labelKey: "integrationsView.type.VIVA" },
  { value: IntegrationType.ELAVON, labelKey: "integrationsView.type.ELAVON" },
  {
    value: IntegrationType.PAYTRIM,
    labelKey: "integrationsView.type.PAYTRIM",
  },
] as const;

export const AcquirersSection = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedIntegrationId, setSelectedIntegrationId] =
    useState<IntegrationId | null>(null);
  const [createIntegrationType, setCreateIntegrationType] = useState("");
  const [formActionError, setFormActionError] = useState(false);
  const [copiedIntegrationId, setCopiedIntegrationId] =
    useState<IntegrationId | null>(null);

  const { data: store, isLoading: isLoadingStore } = useGetStoreQuery(storeId);
  const { data: integrations, isLoading: isLoadingIntegrations } =
    useGetIntegrationsQuery(store?.chainId ?? null);
  const isLoading = isLoadingStore || isLoadingIntegrations;

  const acquirerIntegrations = (integrations ?? []).filter(
    isAcquirerIntegration,
  );
  const selectedIntegration = acquirerIntegrations.find(
    (item) => item.id === selectedIntegrationId,
  );

  const handleCreateClick = () => {
    setFormActionError(false);
    setSelectedIntegrationId(null);
    setCreateIntegrationType("");
    setIsCreating(true);
    setIsPanelOpen(true);
  };

  const handleIntegrationClick = (item: RestIntegration) => {
    setFormActionError(false);
    setSelectedIntegrationId(item.id);
    setIsCreating(false);
    setCreateIntegrationType("");
    setIsPanelOpen(true);
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setIsCreating(false);
    setSelectedIntegrationId(null);
    setCreateIntegrationType("");
    setFormActionError(false);
  };

  const handleFormSuccess = () => {
    handlePanelClose();
  };

  const handleCopyOnboardingUrl = (id: IntegrationId, url: string) => {
    navigator.clipboard
      .writeText(url)
      .then(() => {
        setCopiedIntegrationId(id);
        window.setTimeout(() => {
          setCopiedIntegrationId((current) =>
            current === id ? null : current,
          );
        }, 2000);
      })
      .catch(() => {
        window.alert(url);
      });
  };

  if (isLoading) {
    return (
      <Card>
        <SectionTitle>{t("packageSelection.acquirers")}</SectionTitle>
        <LoadingContainer message={t("packageSelection.loading")} />
      </Card>
    );
  }

  return (
    <Card>
      <SectionHeader>
        <SectionTitle>{t("packageSelection.acquirers")}</SectionTitle>
        <Button onClick={handleCreateClick}>
          {t("packageSelection.addAcquirer")}
        </Button>
      </SectionHeader>

      {acquirerIntegrations.length === 0 ? (
        <EmptyText>{t("packageSelection.noAcquirers")}</EmptyText>
      ) : (
        <AcquirerList>
          {acquirerIntegrations.map((integration) => {
            const onboardingUrl = getVivaOnboardingUrl(integration);
            return (
              <AcquirerRow key={integration.id}>
                <AcquirerOpenButton
                  type="button"
                  onClick={() => handleIntegrationClick(integration)}
                >
                  <AcquirerInfo>
                    <AcquirerName>{integration.name}</AcquirerName>
                    <AcquirerMeta>
                      {t(`integrationsView.type.${integration.type}`)}
                    </AcquirerMeta>
                  </AcquirerInfo>
                </AcquirerOpenButton>
                {onboardingUrl && (
                  <CopyOnboardingButton
                    type="button"
                    onClick={() =>
                      handleCopyOnboardingUrl(integration.id, onboardingUrl)
                    }
                  >
                    {copiedIntegrationId === integration.id ? (
                      <Check size={14} />
                    ) : (
                      <Copy size={14} />
                    )}
                    {copiedIntegrationId === integration.id
                      ? t("common.copiedToClipboard")
                      : t("integrationDetails.vivaCopyOnboardingLink")}
                  </CopyOnboardingButton>
                )}
              </AcquirerRow>
            );
          })}
        </AcquirerList>
      )}

      <SidebarPanel
        isOpen={isPanelOpen}
        onClose={handlePanelClose}
        title={
          isCreating
            ? t("packageSelection.addAcquirer")
            : t("packageSelection.editAcquirer", {
                name: selectedIntegration?.name ?? "",
              })
        }
      >
        {formActionError && (
          <InfoBox variant="error">{t("integrationsView.errorUpdate")}</InfoBox>
        )}
        {isCreating && (
          <FormGroup>
            <FormLabel>{t("integrationForm.typeLabel")}</FormLabel>
            <SelectInput
              value={createIntegrationType}
              onChange={(e) => setCreateIntegrationType(e.target.value)}
              required
            >
              <option value="">
                {t("integrationForm.selectPlaceholder")}
              </option>
              {ACQUIRER_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </SelectInput>
          </FormGroup>
        )}
        {storeId && (selectedIntegration || createIntegrationType) && (
          <IntegrationForm
            storeId={storeId}
            chainId={store?.chainId}
            integration={selectedIntegration}
            integrationType={
              isCreating ? createIntegrationType : undefined
            }
            onSuccess={handleFormSuccess}
            onError={() => setFormActionError(true)}
            onCancel={handlePanelClose}
          />
        )}
      </SidebarPanel>
    </Card>
  );
};
